import React, { useEffect, useState } from 'react';
import { Arzt, Praxis, Zuweisung, Zeitslot, Abwesenheit, Feiertag } from '../../types';
import { arztService } from '../../services/arzt.service';
import { praxisService } from '../../services/praxis.service';
import { zuweisungService } from '../../services/zuweisung.service';
import { abwesenheitService } from '../../services/abwesenheit.service';
import { feiertagService } from '../../services/feiertag.service';
import { formatDate, isSameDay, isDateWithin } from '../../utils/dateUtils';
import AbwesenheitDialog from '../abwesenheit/AbwesenheitDialog';
import FeiertageDialog from '../common/FeiertageDialog';
import { useEnvironment } from '../../context/EnvironmentContext';
import { scheduleService } from '../../services/schedule.service';
import { AppType } from '../../types';

interface WeeklyScheduleViewProps {
  refreshTrigger?: number;
}

const WeeklyScheduleView: React.FC<WeeklyScheduleViewProps> = ({ refreshTrigger }) => {
  const { currentApp } = useEnvironment();
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getMonday(new Date()));
  const [praxen, setPraxen] = useState<Praxis[]>([]);
  const [aerzte, setAerzte] = useState<Arzt[]>([]);
  const [zuweisungen, setZuweisungen] = useState<Zuweisung[]>([]);
  const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);
  const [feiertage, setFeiertage] = useState<Feiertag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAbwesenheitDialogOpen, setIsAbwesenheitDialogOpen] = useState(false);
  const [isFeiertageDialogOpen, setIsFeiertageDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [filterArztId, setFilterArztId] = useState<string>('');
  const [filterPraxisId, setFilterPraxisId] = useState<string>('');

  const weekDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  const hasAfternoonSlot = (day: string) => {
    return !['Mittwoch', 'Freitag', 'Samstag'].includes(day);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, refreshTrigger, filterArztId, filterPraxisId, currentApp]);

  function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 5);

      const filter: any = {
        startDatum: currentWeekStart,
        endDatum: weekEnd,
      };

      if (filterArztId) {
        filter.arztId = parseInt(filterArztId);
      }

      if (filterPraxisId) {
        filter.praxisId = parseInt(filterPraxisId);
      }




      // Let's rewrite the Promise.all section completely
      const promises: Promise<any>[] = [
        praxisService.getAllPraxen(currentApp as AppType),
        arztService.getAllAerzte(currentApp as AppType),
        abwesenheitService.getAllAbwesenheiten({ startDatum: currentWeekStart, endDatum: weekEnd, app: currentApp as AppType }),
        feiertagService.getAllFeiertage({ startDatum: currentWeekStart, endDatum: weekEnd }),
      ];

      if (currentApp === AppType.LEGACY) {
        promises.push(zuweisungService.getAllZuweisungen(filter, 1, 10000));
      } else {
        promises.push(scheduleService.getEffectiveSchedule({
          app: currentApp as AppType,
          startDatum: currentWeekStart,
          endDatum: weekEnd,
          praxisId: filter.praxisId,
          arztId: filter.arztId
        }));
      }

      const [praxenData, aerzteData, abwesenheitenData, feiertageData, assignmentsData] = await Promise.all(promises);

      setPraxen(praxenData);
      setAerzte(aerzteData);
      setAbwesenheiten(abwesenheitenData);
      setFeiertage(feiertageData);

      if (currentApp === AppType.LEGACY) {
        setZuweisungen(Array.isArray(assignmentsData) ? assignmentsData : assignmentsData.data);
      } else {
        setZuweisungen(assignmentsData); // Effective schedule has same shape as Zuweisung[]
      }

    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const getDateForDay = (dayIndex: number): Date => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayIndex);
    return date;
  };

  const getZuweisungenForCell = (praxisId: number, date: Date, zeitslot: Zeitslot): Zuweisung[] => {
    return zuweisungen.filter(
      (z) =>
        z.praxisId === praxisId &&
        isSameDay(z.datum, date) &&
        z.zeitslot === zeitslot
    ).sort((a, b) => {
      const nameA = a.arzt ? `${a.arzt.nachname} ${a.arzt.vorname}` : '';
      const nameB = b.arzt ? `${b.arzt.nachname} ${b.arzt.vorname}` : '';
      return nameA.localeCompare(nameB);
    });
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const handleCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()));
  };

  const handleCellClick = (praxisId: number, date: Date, zeitslot: Zeitslot) => {
    const cellKey = `${praxisId} -${date.toISOString()} -${zeitslot} `;
    setEditingCell(cellKey);
  };

  const handleArztAdd = async (
    praxisId: number,
    date: Date,
    zeitslot: Zeitslot,
    arztId: string
  ) => {
    if (arztId === '') return;

    try {
      const selectedArztId = parseInt(arztId);

      // Create new assignment
      if (currentApp === AppType.LEGACY) {
        await zuweisungService.createZuweisung({
          arztId: selectedArztId,
          praxisId,
          datum: date,
          zeitslot,
        });
      } else {
        // Create Override (Assign Doctor)
        await scheduleService.createOverride({
          arztId: selectedArztId,
          praxisId,
          datum: date,
          zeitslot,
          app: currentApp as AppType,
          isCancelled: false
        });
      }

      setEditingCell(null);
      fetchData();
    } catch (err: any) {
      if (err.response?.status === 409) {
        alert(`Konflikt: ${err.response.data.message} `);
      } else {
        alert('Fehler beim Speichern der Zuweisung');
      }
    }
  };

  const handleArztRemove = async (zuweisung: any) => {
    try {
      if (currentApp === AppType.LEGACY) {
        await zuweisungService.deleteZuweisung(zuweisung.id);
      } else {
        // Create Cancellation Override
        // For new apps, we use the effective schedule.
        // We create a cancellation override for the specific slot.
        // Even if we are deleting an override, creating a cancellation override
        // ensures the slot becomes empty.
        // (Optimisation: If it's an override that doesn't mask a default, we could just delete it.
        // But simply "Cancelling" is safer to ensure it's empty regardless of default state).

        await scheduleService.createOverride({
          arztId: zuweisung.arztId,
          praxisId: zuweisung.praxisId,
          datum: zuweisung.datum,
          zeitslot: zuweisung.zeitslot as Zeitslot,
          app: currentApp as AppType,
          isCancelled: true
        });
      }
      fetchData();
    } catch (err: any) {
      alert('Fehler beim Löschen der Zuweisung');
    }
  };

  const getAvailableAerzte = (praxisId: number, date: Date, zeitslot: Zeitslot) => {
    // Find all doctors already assigned to THIS CELL (same praxis, date, zeitslot)
    const assignedArztIdsInCell = zuweisungen
      .filter(
        (z) =>
          z.praxisId === praxisId &&
          isSameDay(z.datum, date) &&
          z.zeitslot === zeitslot
      )
      .map((z) => z.arztId);

    // Find all doctors assigned to OTHER practices at this date and zeitslot
    const assignedArztIdsElsewhere = zuweisungen
      .filter(
        (z) =>
          z.praxisId !== praxisId &&
          isSameDay(z.datum, date) &&
          z.zeitslot === zeitslot
      )
      .map((z) => z.arztId);

    // Find doctors who are absent on this date
    const absentArztIds = abwesenheiten
      .filter((a) => isDateWithin(date, a.startDatum, a.endDatum))
      .map((a) => a.arztId);

    // Return doctors that are not in this cell, not assigned elsewhere, NOT absent, AND have availability
    return aerzte.filter((arzt) => {
      // 1. Existing checks: Already assigned or absent
      if (assignedArztIdsInCell.includes(arzt.id)) return false;
      if (assignedArztIdsElsewhere.includes(arzt.id)) return false;
      if (absentArztIds.includes(arzt.id)) return false;

      // 2. Availability Check (New Logic)
      const dayName = date.toLocaleDateString('de-DE', { weekday: 'long' });

      if (arzt.praxisAssociations && arzt.praxisAssociations.length > 0) {
        // Modern Logic
        const association = arzt.praxisAssociations.find((a) => a.praxisId === praxisId);

        // Must be associated with this practice
        if (!association) return false;

        // Must work on this day
        if (!association.arbeitstage?.includes(dayName)) return false;

        return true;
      } else if (arzt.praxen && arzt.praxen.length > 0) {
        // Legacy Logic
        const isAssociated = arzt.praxen.some((p) => p.id === praxisId);
        if (!isAssociated) return false;

        if (arzt.arbeitstage && !arzt.arbeitstage.includes(dayName)) return false;

        return true;
      } else {
        // No availability set -> Assume unavailable
        return false;
      }
    });
  };

  const renderCell = (praxisId: number, date: Date, zeitslot: Zeitslot) => {
    // Holiday merging is handled in the main loop now, but we still need this for individual slots
    // if a holiday is NOT GANZTAGS (e.g. VORMITTAG holiday). 
    // However, the loop will handle generic holiday cells too? 
    // Let's keep specific slot holiday logic here for safety if mixed?
    // Actually, plan says: "If GANZTAGS holiday: push cell with colSpan: 2".
    // So distinct logic in loop.
    // But if VORMITTAG holiday, we still render here?
    // Let's remove holiday logic from here to avoid duplication if handled in loop. 
    // Wait, the loop will decide whether to call renderCell or render a holiday cell.

    const cellKey = `${praxisId} -${date.toISOString()} -${zeitslot} `;
    const zuweisungenInCell = getZuweisungenForCell(praxisId, date, zeitslot);
    const isEditing = editingCell === cellKey;

    return (
      <div className="px-2 py-2 text-xs min-h-[3rem]">
        {/* Display assigned doctors */}
        <div className="space-y-1 mb-1">
          {zuweisungenInCell.map((zuweisung) => {
            const isAbsent = abwesenheiten.some(a => a.arztId === zuweisung.arztId && isDateWithin(date, a.startDatum, a.endDatum));

            return (
              <div
                key={zuweisung.id}
                className={`flex items-center justify-between px-2 py-1 rounded group ${isAbsent ? 'bg-red-100 border border-red-200' : 'bg-gray-100'
                  }`}
                title={isAbsent ? 'Arzt ist abwesend/im Urlaub' : ''}
              >
                <span className={`font-medium text-xs ${isAbsent ? 'text-red-800 line-through' : 'text-gray-900'}`}>
                  {zuweisung.arzt
                    ? `${zuweisung.arzt.vorname} ${zuweisung.arzt.nachname} `
                    : 'Unbekannt'}
                </span>
                <button
                  onClick={() => handleArztRemove(zuweisung)}
                  className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                  title="Entfernen"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>

        {/* Add doctor dropdown */}
        {isEditing ? (
          <select
            autoFocus
            value=""
            onChange={(e) => handleArztAdd(praxisId, date, zeitslot, e.target.value)}
            onBlur={() => setEditingCell(null)}
            className="w-full px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none"
          >
            <option value="">-- Arzt hinzufügen --</option>
            {getAvailableAerzte(praxisId, date, zeitslot).map((arzt) => (
              <option key={arzt.id} value={arzt.id}>
                {arzt.vorname} {arzt.nachname}
              </option>
            ))}
          </select>
        ) : (
          <button
            onClick={() => handleCellClick(praxisId, date, zeitslot)}
            className="w-full px-2 py-2 min-h-[44px] text-xs text-blue-600 hover:bg-blue-50 rounded border border-dashed border-blue-300"
          >
            + Arzt hinzufügen
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const weekEnd = new Date(currentWeekStart);
  weekEnd.setDate(weekEnd.getDate() + 5);

  // Filter practices to display
  const displayedPraxen = filterPraxisId
    ? praxen.filter((p) => p.id === parseInt(filterPraxisId))
    : praxen;

  return (
    <div>
      {/* Filters */}
      <div className="bg-white shadow-md rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="filter-arzt" className="block text-sm font-medium text-gray-700 mb-1">
              Filter nach Arzt
            </label>
            <select
              id="filter-arzt"
              value={filterArztId}
              onChange={(e) => setFilterArztId(e.target.value)}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alle Ärzte</option>
              {aerzte.map((arzt) => (
                <option key={arzt.id} value={arzt.id}>
                  {arzt.vorname} {arzt.nachname}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="filter-praxis" className="block text-sm font-medium text-gray-700 mb-1">
              Filter nach Praxis
            </label>
            <select
              id="filter-praxis"
              value={filterPraxisId}
              onChange={(e) => setFilterPraxisId(e.target.value)}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alle Praxen</option>
              {praxen.map((praxis) => (
                <option key={praxis.id} value={praxis.id}>
                  {praxis.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button
              onClick={() => {
                setFilterArztId('');
                setFilterPraxisId('');
              }}
              className="w-full px-4 py-3 min-h-[44px] text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Filter zurücksetzen
            </button>
          </div>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handlePreviousWeek}
            className="px-4 py-3 min-h-[44px] bg-gray-200 hover:bg-gray-300 rounded-md transition-colors text-sm md:text-base"
          >
            ← Vorherige
          </button>
          <button
            onClick={handleCurrentWeek}
            className="px-4 py-3 min-h-[44px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors text-sm md:text-base"
          >
            Aktuelle Woche
          </button>
          <button
            onClick={handleNextWeek}
            className="px-4 py-3 min-h-[44px] bg-gray-200 hover:bg-gray-300 rounded-md transition-colors text-sm md:text-base"
          >
            Nächste →
          </button>
          <button
            onClick={() => setIsAbwesenheitDialogOpen(true)}
            className="px-4 py-3 min-h-[44px] bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors text-sm md:text-base ml-2"
          >
            Abwesenheit verwalten
          </button>
          <button
            onClick={() => setIsFeiertageDialogOpen(true)}
            className="px-4 py-3 min-h-[44px] bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md transition-colors text-sm md:text-base ml-2 flex items-center gap-2"
          >
            <span>🏖️</span> Feiertage verwalten
          </button>
        </div>
        <div className="text-base md:text-lg font-semibold text-gray-800">
          {formatDate(currentWeekStart)} - {formatDate(weekEnd)}
        </div>
      </div>

      {/* Weekly Schedule Table */}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto overflow-y-visible mobile-scroll">
        <div className="min-w-max">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10">
                  Praxis
                </th>
                {weekDays.map((day, index) => (
                  <th
                    key={day}
                    colSpan={hasAfternoonSlot(day) ? 2 : 1}
                    className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700"
                  >
                    <div>{day}</div>
                    <div className="text-xs font-normal text-gray-500">
                      {formatDate(getDateForDay(index))}
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 sticky left-0 bg-gray-100 z-10"></th>
                {weekDays.map((day) => (
                  <React.Fragment key={`${day} -slots`}>
                    <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-blue-700">
                      VM
                    </th>
                    {hasAfternoonSlot(day) && (
                      <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-purple-700">
                        NM
                      </th>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedPraxen.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                  >
                    {filterPraxisId
                      ? 'Keine Zuweisungen für die ausgewählte Praxis.'
                      : 'Keine Praxen vorhanden. Bitte erstellen Sie zuerst Praxen.'}
                  </td>
                </tr>
              ) : (
                displayedPraxen.map((praxis) => (
                  <tr key={praxis.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium text-sm text-gray-900 sticky left-0 bg-white z-10">
                      {praxis.name}
                    </td>
                    {weekDays.map((day, dayIndex) => {
                      const date = getDateForDay(dayIndex);

                      // Check for full day holiday
                      const fullDayHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'GANZTAGS';
                      });

                      // Check for partial holidays
                      const vmHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'VORMITTAG';
                      });

                      const nmHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'NACHMITTAG';
                      });

                      if (fullDayHoliday) {
                        return (
                          <td
                            key={`${praxis.id}-${day}-holiday`}
                            colSpan={hasAfternoonSlot(day) ? 2 : 1}
                            className="border border-gray-300 bg-white p-0 align-middle"
                          >
                            <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                              <span className="font-semibold text-orange-800">
                                {fullDayHoliday.name || 'FEIERTAG'}
                              </span>
                            </div>
                          </td>
                        );
                      }

                      return (
                        <React.Fragment key={`${praxis.id}-${day}`}>
                          <td className="border border-gray-300 bg-blue-50">
                            <div className="md:hidden text-xs font-semibold text-blue-800 mb-1">Vormittag</div>
                            {vmHoliday ? (
                              <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                                <span className="font-semibold text-orange-800">{vmHoliday.name || 'FEIERTAG'}</span>
                              </div>
                            ) : (
                              renderCell(praxis.id, date, Zeitslot.VORMITTAG)
                            )}
                          </td>
                          {hasAfternoonSlot(day) && (
                            <td className="border border-gray-300 bg-purple-50">
                              <div className="md:hidden text-xs font-semibold text-purple-800 mb-1">Nachmittag</div>
                              {nmHoliday ? (
                                <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                                  <span className="font-semibold text-orange-800">{nmHoliday.name || 'FEIERTAG'}</span>
                                </div>
                              ) : (
                                renderCell(praxis.id, date, Zeitslot.NACHMITTAG)
                              )}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-50 border border-gray-300"></div>
          <span>Vormittag (07:30 - 13:00)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-50 border border-gray-300"></div>
          <span>Nachmittag (14:00 - 18:30)</span>
        </div>
        <div className="ml-4 text-gray-500">
          💡 Klicken Sie auf eine Zelle, um einen Arzt zuzuweisen
        </div>
      </div>

      {/* Abwesenheiten / Urlaub */}
      {abwesenheiten.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-semibold mb-2">Urlaub / Abwesenheiten:</h3>
          <div className="flex flex-wrap gap-2">
            {abwesenheiten.map((abwesenheit) => (
              <span key={abwesenheit.id} className="text-red-700 text-sm font-medium bg-white px-2 py-1 rounded border border-red-100">
                {abwesenheit.arzt
                  ? `${abwesenheit.arzt.vorname} ${abwesenheit.arzt.nachname}`
                  : 'Unbekannt'} ({abwesenheit.typ}: {formatDate(new Date(abwesenheit.startDatum))} - {formatDate(new Date(abwesenheit.endDatum))})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <AbwesenheitDialog
        isOpen={isAbwesenheitDialogOpen}
        onClose={() => setIsAbwesenheitDialogOpen(false)}
        onUpdate={fetchData}
      />

      <FeiertageDialog
        isOpen={isFeiertageDialogOpen}
        onClose={() => setIsFeiertageDialogOpen(false)}
        onUpdate={fetchData}
      />
    </div>
  );
};

export default WeeklyScheduleView;
